import React from 'react';
import { ChatChartSpecDto, ChatTableSpecDto } from 'src/api/FiretrolApiClient';
import {
  TABLE_EMPTY_TEXT,
  tableDataGrid,
  tableTruncationText,
} from 'src/app/chat/chatTableFormat';
import { entityRefUrl } from 'src/app/chat/entityRefs';
import {
  planPrintChart,
  PRINT_CHART_TOO_TALL_TEXT,
} from 'src/app/chat/printChartPlan';
import { TranscriptPrintChart } from 'src/app/chat/TranscriptPrintChart';
import type { ITranscriptPrintPalette } from 'src/app/chat/TranscriptPrintDocument';
import {
  CHART_UNAVAILABLE_TEXT,
  chartDataTable,
} from 'src/app/chat/transcriptSegmentFormat';

const PrintDataTable: React.FC<{
  header: string[];
  numeric: boolean[];
  rows: string[][];
  cellHref?: (rowIndex: number, cellIndex: number) => string | null;
}> = ({ header, numeric, rows, cellHref }) => {
  const cellClass = (cellIndex: number) =>
    numeric[cellIndex] ? 'num' : undefined;
  return (
    <table className="data-table">
      <thead>
        <tr>
          {header.map((cell, cellIndex) => (
            <th key={cellIndex} className={cellClass(cellIndex)}>
              {cell}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, cellIndex) => {
              const url = cellHref?.(rowIndex, cellIndex) ?? null;
              return (
                <td key={cellIndex} className={cellClass(cellIndex)}>
                  {url === null ? cell : <a href={url}>{cell}</a>}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const TableSection: React.FC<{
  spec: ChatTableSpecDto;
  appOrigin: string | undefined;
}> = ({ spec, appOrigin }) => {
  const grid = tableDataGrid(spec);
  const columns = spec.columns ?? [];
  return (
    <section className="table">
      {spec.title && <p className="table-title">{spec.title}</p>}
      {grid.rows.length > 0 && grid.header.length > 0 ? (
        <PrintDataTable
          header={grid.header}
          numeric={grid.numeric}
          rows={grid.rows}
          cellHref={(rowIndex, cellIndex) =>
            entityRefUrl(columns[cellIndex].refs?.[rowIndex], appOrigin)
          }
        />
      ) : (
        !spec.truncated && <p className="note">{TABLE_EMPTY_TEXT}</p>
      )}
      {spec.truncated && (
        <p className="note">{tableTruncationText(grid.rows.length)}</p>
      )}
    </section>
  );
};

export const ChartSection: React.FC<{
  spec: ChatChartSpecDto;
  palette: ITranscriptPrintPalette;
}> = ({ spec, palette }) => {
  const table = chartDataTable(spec);
  const plan = planPrintChart(spec);
  return (
    <section className="chart">
      <div className="chart-figure">
        <p className="chart-title">{spec.title}</p>
        {(plan.kind === 'pie' || plan.kind === 'cartesian') && (
          <TranscriptPrintChart plan={plan} palette={palette} />
        )}
      </div>
      {plan.kind === 'too-tall' && (
        <p className="note">{PRINT_CHART_TOO_TALL_TEXT}</p>
      )}
      {table ? (
        <PrintDataTable
          header={table.header}
          numeric={table.header.map((_, cellIndex) => cellIndex > 0)}
          rows={table.rows}
        />
      ) : (
        <p className="note">{CHART_UNAVAILABLE_TEXT}</p>
      )}
    </section>
  );
};
